import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTimezone } from '../contexts/TimezoneContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { GameCard } from '../components/GameCard';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Switch } from '../components/ui/switch';
import { 
  CalendarIcon, 
  Filter, 
  RefreshCw, 
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Flame,
  Heart,
  Star,
  Target,
  TrendingUp,
  Award,
  Globe,
  CheckCircle,
  Search
} from 'lucide-react';
import { format, addDays, subDays, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useSEO, SEO_KEYWORDS } from '../utils/seo';
import { Input } from '../components/ui/input';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Use MST as default timezone for consistency with server
const MST_TIMEZONE = 'America/Denver';

// Get today's date string in MST (YYYY-MM-DD)
const getTodayMST = () => {
  return formatInTimeZone(new Date(), MST_TIMEZONE, 'yyyy-MM-dd');
};

// Helper to format date for API (YYYY-MM-DD)
const formatDateForAPI = (date) => {
  // If it's already a string in YYYY-MM-DD format, return as-is
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }
  // Otherwise format the date
  return format(date, 'yyyy-MM-dd');
};

export default function Board() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { isAuthenticated, loading: authLoading, favoriteTeams, favoriteLeagues, toggleFavoriteLeague } = useAuth();
  const { timezone, timezoneAbbr, updateTimezone, timezoneOptions } = useTimezone();
  
  const [games, setGames] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [accuracy, setAccuracy] = useState(null);
  const [dailyAccuracy, setDailyAccuracy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Refs for game cards to enable scroll-to functionality
  const gameCardRefs = useRef({});
  
  const [selectedDate, setSelectedDate] = useState(() => {
    const dateParam = searchParams.get('date');
    if (dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam)) {
      // Use the date string directly, create Date at noon local time
      return parseISO(dateParam);
    }
    // Default to today in MST
    return parseISO(getTodayMST());
  });
  
  // Store the date as a string for API calls
  const [selectedDateStr, setSelectedDateStr] = useState(() => {
    const dateParam = searchParams.get('date');
    return dateParam || getTodayMST();
  });
  const [selectedLeague, setSelectedLeague] = useState(searchParams.get('league') || 'all');
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [confidenceFilter, setConfidenceFilter] = useState('all'); // 'all', 'high', 'medium', 'low'
  const [resultFilter, setResultFilter] = useState('all'); // 'all', 'correct', 'incorrect'
  const [teamSearch, setTeamSearch] = useState(''); // Team search query
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showTimezoneSelect, setShowTimezoneSelect] = useState(false);

  const [refreshInfo, setRefreshInfo] = useState(null);

  // SEO for Board page - dynamic based on selected league
  useSEO({
    title: selectedLeague === 'all' 
      ? "Today's Sports Predictions | Game Picks & Win Probability" 
      : `${selectedLeague.toUpperCase()} Predictions Today | Who Will Win?`,
    description: selectedLeague === 'all'
      ? "View today's AI-powered predictions for NHL, NBA, NFL, MLB games. Get win probabilities, injury reports, and expert picks for every game."
      : `Get ${selectedLeague.toUpperCase()} predictions for today's games. AI-powered win probability analysis, player matchups, and expert picks.`,
    keywords: selectedLeague === 'all' ? SEO_KEYWORDS.board : SEO_KEYWORDS[selectedLeague] || SEO_KEYWORDS.board,
    canonicalUrl: 'https://sportsanalysisab.ca/board'
  });
  
  // Scroll to specific game card when returning from game detail page
  useEffect(() => {
    const scrollToGameId = location.state?.scrollToGame;
    if (scrollToGameId && games.length > 0) {
      // Wait for DOM to render then scroll to the specific game card
      setTimeout(() => {
        const gameElement = gameCardRefs.current[scrollToGameId];
        if (gameElement) {
          gameElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Add a brief highlight effect
          gameElement.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
          setTimeout(() => {
            gameElement.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
          }, 2000);
        }
      }, 300);
      // Clear the state after scrolling
      window.history.replaceState({}, document.title);
    }
  }, [games, location.state]);
  
  // Scroll to specific league section when coming back from game detail
  useEffect(() => {
    const leagueParam = searchParams.get('league');
    if (leagueParam && leagueParam !== 'all' && games.length > 0 && !location.state?.scrollToGame) {
      // Wait for DOM to render then scroll
      setTimeout(() => {
        const leagueElement = document.getElementById(`league-${leagueParam}`);
        if (leagueElement) {
          leagueElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    }
  }, [games, searchParams, location.state]);

  // Require authentication to view games
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Fetch leagues (with cache-busting)
  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        // Add timestamp to prevent caching issues
        const response = await fetch(`${API_URL}/api/leagues?_t=${Date.now()}`, {
          cache: 'no-store'
        });
        if (response.ok) {
          const data = await response.json();
          console.log('[Board] Fetched leagues:', data.length, data.map(l => l.league_id));
          setLeagues(data);
        }
      } catch (err) {
        console.error('Failed to fetch leagues:', err);
      }
    };
    fetchLeagues();
  }, []);

  // Fetch refresh info
  useEffect(() => {
    const fetchRefreshInfo = async () => {
      try {
        const response = await fetch(`${API_URL}/api/games/refresh-info`);
        if (response.ok) {
          const data = await response.json();
          setRefreshInfo(data);
        }
      } catch (err) {
        console.error('Failed to fetch refresh info:', err);
      }
    };
    fetchRefreshInfo();
  }, []);

  // Fetch accuracy stats
  useEffect(() => {
    const fetchAccuracy = async () => {
      try {
        const response = await fetch(`${API_URL}/api/accuracy`);
        if (response.ok) {
          const data = await response.json();
          setAccuracy(data);
        }
      } catch (err) {
        console.error('Failed to fetch accuracy:', err);
      }
    };
    fetchAccuracy();
  }, []);

  // Sync selectedDateStr when selectedDate changes
  useEffect(() => {
    const dateStr = formatDateForAPI(selectedDate);
    setSelectedDateStr(dateStr);
  }, [selectedDate]);

  // Fetch games
  useEffect(() => {
    const fetchGames = async () => {
      setLoading(true);
      setError(null);
      
      try {
        let url = `${API_URL}/api/games?date=${selectedDateStr}&timezone=MST`;
        if (selectedLeague && selectedLeague !== 'all') {
          url += `&league_id=${selectedLeague}`;
        }
        if (favoritesOnly && isAuthenticated) {
          url += `&favorites_only=true`;
        }
        
        console.log('[Board] Fetching games for date:', selectedDateStr, 'URL:', url);
        
        const response = await fetch(url, {
          credentials: 'include'
        });
        if (!response.ok) {
          throw new Error('Failed to fetch games');
        }
        
        const data = await response.json();
        setGames(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchGames();
    
    // Update URL params
    const params = new URLSearchParams();
    params.set('date', selectedDateStr);
    if (selectedLeague && selectedLeague !== 'all') {
      params.set('league', selectedLeague);
    }
    setSearchParams(params);
  }, [selectedDateStr, selectedLeague, favoritesOnly, isAuthenticated, setSearchParams]);

  // Fetch daily accuracy for selected date
  useEffect(() => {
    const fetchDailyAccuracy = async () => {
      try {
        const response = await fetch(`${API_URL}/api/accuracy/daily/${selectedDateStr}`);
        if (response.ok) {
          const data = await response.json();
          setDailyAccuracy(data);
        } else {
          setDailyAccuracy(null);
        }
      } catch (err) {
        console.error('Failed to fetch daily accuracy:', err);
        setDailyAccuracy(null);
      }
    };
    fetchDailyAccuracy();
  }, [selectedDateStr]);

  const handleDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
      setCalendarOpen(false);
    }
  };

  const handlePrevDay = () => {
    const newDate = subDays(selectedDate, 1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = addDays(selectedDate, 1);
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    const todayStr = getTodayMST();
    setSelectedDateStr(todayStr);
    setSelectedDate(parseISO(todayStr));
  };

  // Manual refresh live scores
  const handleRefreshLiveScores = async () => {
    setIsRefreshing(true);
    try {
      // Refresh the games list
      const dateStr = formatDateForAPI(selectedDate);
      let url = `${API_URL}/api/games?date=${dateStr}&timezone=MST`;
      if (selectedLeague && selectedLeague !== 'all') {
        url += `&league_id=${selectedLeague}`;
      }
      
      const response = await fetch(url, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setGames(data);
      }
    } catch (err) {
      console.error('Failed to refresh:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Get user's timezone for display
  const userTimezone = 'MST (Mountain Time)';

  // Check if selected date is in the past (for result filtering)
  const isPastDate = selectedDate < new Date(new Date().setHours(0, 0, 0, 0));

  // Filter games by confidence and result
  const filteredGames = games.filter(game => {
    // Confidence filter
    if (confidenceFilter !== 'all') {
      const gameConfidence = (game.confidence || '').toLowerCase();
      if (gameConfidence !== confidenceFilter) return false;
    }
    
    // Result filter (only for finished games)
    if (resultFilter !== 'all' && game.status === 'final') {
      const isCorrect = game.prediction_correct;
      if (resultFilter === 'correct' && !isCorrect) return false;
      if (resultFilter === 'incorrect' && isCorrect) return false;
    }
    
    // Team search filter
    if (teamSearch.trim()) {
      const searchLower = teamSearch.toLowerCase().trim();
      const homeTeam = (game.home_team?.name || game.home_team?.abbreviation || '').toLowerCase();
      const awayTeam = (game.away_team?.name || game.away_team?.abbreviation || '').toLowerCase();
      if (!homeTeam.includes(searchLower) && !awayTeam.includes(searchLower)) {
        return false;
      }
    }
    
    return true;
  });

  // Group filtered games by league, already sorted by time from API
  const gamesByLeague = filteredGames.reduce((acc, game) => {
    const leagueName = game.league_name || 'Other';
    if (!acc[leagueName]) {
      acc[leagueName] = [];
    }
    acc[leagueName].push(game);
    return acc;
  }, {});

  // Sort each league's games by start time (earliest first)
  Object.keys(gamesByLeague).forEach(league => {
    gamesByLeague[league].sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
  });

  // Featured games (high confidence, significant probability)
  const featuredGames = filteredGames.filter(g => g.is_featured).sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

  // Format last refresh time
  const formatLastRefresh = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  // Don't render if not authenticated or loading auth state
  if (authLoading || !isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <main className="flex-1">
        {/* Header */}
        <div className="border-b border-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight">
                  Game Board
                </h1>
                <p className="text-muted-foreground mt-1">
                  Statistical previews and probability estimates
                  <span className="text-xs ml-2 opacity-70">• Times shown in {userTimezone}</span>
                  {refreshInfo?.last_refresh && (
                    <span className="text-xs ml-2 opacity-70">• Updated {formatLastRefresh(refreshInfo.last_refresh)}</span>
                  )}
                </p>
              </div>
              
              {/* Accuracy Badge */}
              {accuracy && accuracy.total_predictions > 0 && (
                <div 
                  className="flex items-center gap-3 bg-card border border-border rounded-sm px-4 py-2 cursor-pointer hover:border-muted-foreground/50 transition-colors"
                  onClick={() => navigate('/accuracy')}
                  data-testid="accuracy-badge"
                >
                  <Target className="w-5 h-5 text-red-400" />
                  <div>
                    <p className="text-xs text-muted-foreground">Platform Accuracy</p>
                    <p className="font-mono font-bold text-lg">
                      <span className={accuracy.accuracy_percentage >= 55 ? 'text-green-400' : 'text-yellow-400'}>
                        {accuracy.accuracy_percentage.toFixed(1)}%
                      </span>
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    ({accuracy.total_predictions} games)
                  </span>
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="flex flex-wrap items-center gap-2 mt-4">
              {/* Date navigation */}
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-sm h-9 w-9"
                  onClick={handlePrevDay}
                  data-testid="prev-day-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                
                <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="rounded-sm min-w-[140px] justify-start gap-2"
                      data-testid="date-picker-btn"
                    >
                      <CalendarIcon className="w-4 h-4" />
                      {format(selectedDate, 'MMM d, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={handleDateChange}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-sm h-9 w-9"
                  onClick={handleNextDay}
                  data-testid="next-day-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>

              <Button
                variant="outline"
                size="sm"
                className="rounded-sm"
                onClick={handleToday}
                data-testid="today-btn"
              >
                Today
              </Button>

              {/* League filter */}
              <Select value={selectedLeague} onValueChange={setSelectedLeague}>
                <SelectTrigger className="w-[140px] rounded-sm" data-testid="league-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Leagues" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Leagues</SelectItem>
                  {leagues.map((league) => (
                    <SelectItem key={league.league_id} value={league.league_id}>
                      <div className="flex items-center gap-2">
                        {favoriteLeagues.includes(league.league_id) && (
                          <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                        )}
                        {league.abbreviation}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Team search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search team..."
                  value={teamSearch}
                  onChange={(e) => setTeamSearch(e.target.value)}
                  className="pl-9 w-[160px] rounded-sm h-9"
                  data-testid="team-search-input"
                />
              </div>

              {/* Confidence filter */}
              <Select value={confidenceFilter} onValueChange={setConfidenceFilter}>
                <SelectTrigger className="w-[150px] rounded-sm" data-testid="confidence-filter">
                  <Target className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="All Confidence" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Confidence</SelectItem>
                  <SelectItem value="high">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-500"></span>
                      High Confidence
                    </div>
                  </SelectItem>
                  <SelectItem value="medium">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                      Medium Confidence
                    </div>
                  </SelectItem>
                  <SelectItem value="low">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-red-500"></span>
                      Low Confidence
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>

              {/* Result filter - only show for past dates with finished games */}
              {isPastDate && (
                <Select value={resultFilter} onValueChange={setResultFilter}>
                  <SelectTrigger className="w-[150px] rounded-sm" data-testid="result-filter">
                    <CheckCircle className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="All Results" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Results</SelectItem>
                    <SelectItem value="correct">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        Correct Only
                      </div>
                    </SelectItem>
                    <SelectItem value="incorrect">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-red-500"></span>
                        Incorrect Only
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              )}

              {/* Favorites filter */}
              {isAuthenticated && (favoriteTeams.length > 0 || favoriteLeagues.length > 0) && (
                <div className="flex items-center gap-2 bg-secondary/50 rounded-sm px-3 py-1.5">
                  <Heart className={`w-4 h-4 ${favoritesOnly ? 'text-red-400 fill-red-400' : 'text-muted-foreground'}`} />
                  <span className="text-sm">Favorites</span>
                  <Switch
                    checked={favoritesOnly}
                    onCheckedChange={setFavoritesOnly}
                    data-testid="favorites-toggle"
                  />
                </div>
              )}

              {/* Timezone selector */}
              <Popover open={showTimezoneSelect} onOpenChange={setShowTimezoneSelect}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="rounded-sm gap-2"
                    data-testid="timezone-selector"
                  >
                    <Globe className="w-4 h-4" />
                    <span className="hidden sm:inline">{timezoneAbbr}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-2" align="end">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground px-2 py-1">Select Timezone</p>
                    {timezoneOptions.map((tz) => (
                      <button
                        key={tz.value}
                        onClick={() => {
                          updateTimezone(tz.value);
                          setShowTimezoneSelect(false);
                        }}
                        className={`w-full text-left px-2 py-1.5 rounded-sm text-sm hover:bg-secondary transition-colors ${
                          timezone === tz.value ? 'bg-secondary font-medium' : ''
                        }`}
                      >
                        {tz.label}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Refresh button */}
              <Button
                variant="outline"
                size="sm"
                className="rounded-sm ml-auto"
                onClick={handleRefreshLiveScores}
                disabled={isRefreshing}
                data-testid="refresh-scores-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                {isRefreshing ? 'Refreshing...' : 'Refresh Scores'}
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Daily Accuracy Banner - Show when there's accuracy data for selected date */}
          {dailyAccuracy && dailyAccuracy.total_predictions > 0 && (
            <div 
              className="mb-6 bg-gradient-to-r from-green-500/10 via-emerald-500/10 to-green-500/10 border border-green-500/30 rounded-sm p-4 cursor-pointer hover:border-green-500/50 transition-all"
              onClick={() => navigate('/accuracy')}
              data-testid="daily-accuracy-banner"
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-green-500/20 rounded-full flex items-center justify-center">
                    <Award className="w-7 h-7 text-green-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm text-muted-foreground uppercase tracking-wider font-medium">
                        {format(selectedDate, 'EEEE, MMM d')} Accuracy
                      </p>
                      {dailyAccuracy.accuracy_percentage >= 60 && (
                        <Badge className="bg-green-500 text-white text-xs">HOT</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="font-display text-3xl font-bold">
                        <span className={dailyAccuracy.accuracy_percentage >= 55 ? 'text-green-400' : dailyAccuracy.accuracy_percentage >= 50 ? 'text-yellow-400' : 'text-red-400'}>
                          {dailyAccuracy.accuracy_percentage.toFixed(1)}%
                        </span>
                      </p>
                      <span className="text-sm text-muted-foreground">
                        ({dailyAccuracy.correct_predictions}/{dailyAccuracy.total_predictions} correct)
                      </span>
                    </div>
                    {/* Timezone indicator */}
                    <p className="text-xs text-muted-foreground mt-1">
                      As of {formatInTimeZone(new Date(), MST_TIMEZONE, 'h:mm a')} MST ({selectedDateStr})
                    </p>
                  </div>
                </div>
                
                {/* Per-League Breakdown */}
                {Object.keys(dailyAccuracy.by_league || {}).length > 0 && (
                  <div className="flex flex-wrap items-center gap-3">
                    {Object.entries(dailyAccuracy.by_league)
                      .filter(([_, data]) => data.total > 0)
                      .sort((a, b) => b[1].accuracy - a[1].accuracy)
                      .map(([league, data]) => (
                        <div 
                          key={league} 
                          className="flex items-center gap-2 px-3 py-2 bg-secondary/50 rounded-sm"
                        >
                          <span className="font-mono text-xs font-bold uppercase">{league}</span>
                          <span className={`font-mono text-sm font-bold ${data.accuracy >= 60 ? 'text-green-400' : data.accuracy >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                            {data.accuracy.toFixed(0)}%
                          </span>
                          <span className="text-xs text-muted-foreground">
                            ({data.correct}/{data.total})
                          </span>
                        </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Loading state */}
          {loading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-card border border-border rounded-sm p-4">
                  <Skeleton className="h-4 w-24 mb-4" />
                  <Skeleton className="h-16 w-full mb-2" />
                  <Skeleton className="h-16 w-full mb-4" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))}
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
              <h3 className="font-display text-xl font-bold uppercase mb-2">Error Loading Games</h3>
              <p className="text-muted-foreground mb-4">{error}</p>
              <Button 
                variant="outline" 
                className="rounded-sm"
                onClick={() => window.location.reload()}
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Retry
              </Button>
            </div>
          )}

          {/* No games */}
          {!loading && !error && games.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12">
              <CalendarIcon className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="font-display text-xl font-bold uppercase mb-2">No Games Found</h3>
              <p className="text-muted-foreground mb-4">
                {favoritesOnly 
                  ? 'No games from your favorite teams/leagues on this date'
                  : `No games scheduled for ${format(selectedDate, 'MMMM d, yyyy')}`
                }
              </p>
              <div className="flex gap-2">
                {favoritesOnly && (
                  <Button 
                    variant="outline" 
                    className="rounded-sm"
                    onClick={() => setFavoritesOnly(false)}
                  >
                    Show All Games
                  </Button>
                )}
                <Button 
                  variant="outline" 
                  className="rounded-sm"
                  onClick={handleToday}
                >
                  View Today's Games
                </Button>
              </div>
            </div>
          )}

          {/* Featured games */}
          {!loading && !error && featuredGames.length > 0 && (
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <Flame className="w-5 h-5 text-red-400" />
                <h2 className="font-display text-xl font-bold uppercase tracking-tight">
                  Featured Matchups
                </h2>
                <Badge className="confidence-high text-xs uppercase">High Confidence</Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {featuredGames.map((game) => (
                  <div 
                    key={game.game_id} 
                    ref={el => gameCardRefs.current[game.game_id] = el}
                    className="transition-all duration-300"
                  >
                    <GameCard game={game} />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Games by league */}
          {!loading && !error && Object.keys(gamesByLeague).length > 0 && (
            <div className="space-y-8">
              {Object.entries(gamesByLeague).map(([leagueName, leagueGames]) => {
                const leagueData = leagues.find(l => l.name === leagueName);
                const isFavorite = leagueData && favoriteLeagues.includes(leagueData.league_id);
                
                return (
                  <div key={leagueName} id={leagueData ? `league-${leagueData.league_id}` : undefined}>
                    <div className="flex items-center gap-2 mb-4">
                      <h2 className="font-display text-xl font-bold uppercase tracking-tight">
                        {leagueName}
                      </h2>
                      {isAuthenticated && leagueData && (
                        <button
                          onClick={() => toggleFavoriteLeague(leagueData.league_id)}
                          className="p-1 hover:bg-secondary rounded-sm transition-colors"
                          data-testid={`favorite-league-${leagueData.league_id}`}
                        >
                          <Star className={`w-4 h-4 ${isFavorite ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground'}`} />
                        </button>
                      )}
                      <Badge variant="outline" className="font-mono text-xs">
                        {leagueGames.length} {leagueGames.length === 1 ? 'game' : 'games'}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {leagueGames.map((game) => (
                        <div 
                          key={game.game_id} 
                          ref={el => gameCardRefs.current[game.game_id] = el}
                          className="transition-all duration-300"
                        >
                          <GameCard game={game} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <div className="border-t border-border bg-card/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <p className="text-xs text-muted-foreground text-center">
              For informational and entertainment purposes only. Not betting advice. 
              Probability estimates are statistical projections, not guarantees.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
